export const BRAND_CMS_SLUG = 'the-brand-cms';
export const BRAND_CMS_PATH = '/the-brand';

export const PROCESS_CMS_SLUG = 'our-process-cms';
export const PROCESS_CMS_PATH = '/our-process';

export const TRADE_CMS_SLUG = 'trade-cms';
export const TRADE_CMS_PATH = '/trade';

export const STUDIO_CMS_SLUG = 'studio-cms';
export const STUDIO_CMS_PATH = '/studio';

export const BRAND_CMS_SECTION_TYPES = [
  { type: 'hero', label: 'Hero' },
  { type: 'intro_quote', label: 'Intro quote' },
  { type: 'indexed_text_image', label: 'Indexed text + image' },
  { type: 'craftsmanship_gallery', label: 'Craftsmanship gallery' },
  { type: 'showroom', label: 'Showroom' },
  { type: 'environment', label: 'Environment' },
  { type: 'consultation_cta', label: 'Consultation CTA' },
];

export const PROCESS_CMS_SECTION_TYPES = [
  { type: 'hero', label: 'Hero' },
  { type: 'intro_quote', label: 'Intro quote' },
  { type: 'process_text_image', label: 'Process text + image' },
  { type: 'process_build', label: 'Process build' },
  { type: 'process_approvals', label: 'Process approvals' },
  { type: 'process_unique', label: 'Process unique' },
  { type: 'consultation_cta', label: 'Consultation CTA' },
];

/** Trade-first defaults, then Brand + Process types (deduped by `type`). */
export const TRADE_CMS_SECTION_TYPES = (() => {
  const tradeTypes = [
    { type: 'trade_intro', label: 'Trade intro' },
    { type: 'trade_card', label: 'Trade card' },
    { type: 'trade_cta', label: 'Trade CTA' },
  ];
  const seen = new Set(tradeTypes.map(item => item.type));
  const merged = [...tradeTypes];
  [...BRAND_CMS_SECTION_TYPES, ...PROCESS_CMS_SECTION_TYPES].forEach(item => {
    if (seen.has(item.type)) return;
    seen.add(item.type);
    merged.push(item);
  });
  return merged;
})();

/** Studio-first defaults, then Trade / Brand / Process types (deduped by `type`). */
export const STUDIO_CMS_SECTION_TYPES = (() => {
  const studioTypes = [
    { type: 'trade_intro', label: 'Studio intro' },
    { type: 'studio_showrooms', label: 'Featured showrooms' },
    { type: 'indexed_text_image', label: 'Indexed text + image' },
  ];
  const seen = new Set(studioTypes.map(item => item.type));
  const merged = [...studioTypes];
  TRADE_CMS_SECTION_TYPES.forEach(item => {
    if (seen.has(item.type)) return;
    seen.add(item.type);
    merged.push(item);
  });
  return merged;
})();

/** @deprecated Prefer BRAND_CMS_SECTION_TYPES, PROCESS_CMS_SECTION_TYPES, or TRADE_CMS_SECTION_TYPES */
export const MARKETING_PAGE_SECTION_TYPES = BRAND_CMS_SECTION_TYPES;

const INDEXED_TYPES = new Set([
  'indexed_text_image',
  'craftsmanship_gallery',
  'showroom',
  'environment',
  'process_text_image',
  'process_build',
  'process_approvals',
  'process_unique',
]);

export function resolvedImageUrl(value) {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value.url ?? value.fileUrl ?? null;
}

export function resolvedImageAlt(value, fallback = '') {
  if (!value || typeof value !== 'object') return fallback;
  return value.altText || value.title || fallback;
}

export function resolvedImageId(value) {
  if (value == null) return null;
  if (typeof value === 'object') return value.id ?? null;
  return value;
}

/** Prefer Brand-style imageAssetId; accept contract alias `image`. */
export function sectionImageValue(data) {
  if (!data || typeof data !== 'object') return null;
  return data.imageAssetId ?? data.image ?? null;
}

/** Sticky index entries from CMS sections with includeInIndex. */
export function indexEntriesFromSections(sections) {
  return (sections ?? [])
    .filter(
      section =>
        INDEXED_TYPES.has(section?.type) &&
        section?.data?.includeInIndex !== false &&
        section?.data?.indexId,
    )
    .map(section => ({
      id: section.data.indexId,
      num: section.data.indexNum || '',
      label: section.data.indexLabel || section.data.heading || section.data.indexId,
    }));
}

export function indexSpyIdsFromSections(sections) {
  return indexEntriesFromSections(sections).map(entry => entry.id);
}

export function createSectionId(type) {
  const suffix =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().slice(0, 8)
      : String(Date.now());
  return `${type}-${suffix}`;
}

function mapImageList(items) {
  return (items || []).map(item => ({
    imageAssetId: resolvedImageId(item?.imageAssetId ?? item?.image ?? item),
    alt: typeof item === 'object' && item ? item.alt ?? '' : '',
  }));
}

export function defaultSectionData(type) {
  switch (type) {
    case 'hero':
      return { eyebrow: '', headline: 'NIICO', tagline: '', subheadline: '', imageAssetId: null };
    case 'intro_quote':
      return { quote: '' };
    case 'indexed_text_image':
      return {
        indexId: 'section',
        indexLabel: 'Section',
        indexNum: '01',
        eyebrow: '',
        heading: '',
        body: '',
        imageAssetId: null,
        imageAlt: '',
        imageSide: 'right',
        includeInIndex: true,
      };
    case 'craftsmanship_gallery':
      return {
        indexId: 'craftsmanship',
        indexLabel: 'Elevated Craftsmanship',
        indexNum: '04',
        eyebrow: '',
        heading: '',
        body: '',
        images: [],
        includeInIndex: true,
      };
    case 'showroom':
      return {
        indexId: 'showroom',
        indexLabel: 'Visit the Showroom',
        indexNum: '05',
        eyebrow: '',
        heading: '',
        body: '',
        address: '',
        hours: '',
        imageAssetId: null,
        imageAlt: '',
        mapsUrl: '',
        ctaLabel: 'Book a Visit',
        includeInIndex: true,
      };
    case 'environment':
      return {
        indexId: 'environment',
        indexLabel: 'The Environment',
        indexNum: '06',
        eyebrow: '',
        heading: '',
        body: '',
        backgroundImageAssetId: null,
        bullets: [],
        logos: [],
        includeInIndex: true,
      };
    case 'consultation_cta':
      return {
        heading: 'Book an Appointment',
        body: '',
        ctaLabel: 'Book Now',
        ctaUrl: '/book-online',
        imageAssetId: null,
        imageAlt: '',
      };
    case 'process_text_image':
      return {
        indexId: 'design',
        indexLabel: 'Design',
        indexNum: '01',
        eyebrow: '',
        heading: '',
        paragraphs: [''],
        listHeading: '',
        items: [{ title: '', body: '' }],
        imageAssetId: null,
        includeInIndex: true,
      };
    case 'process_build':
      return {
        indexId: 'build',
        indexLabel: 'Build',
        indexNum: '02',
        eyebrow: '',
        heading: '',
        paragraphs: [''],
        stages: [{ title: '', lead: '', body: '' }],
        imageAssetId: null,
        includeInIndex: true,
      };
    case 'process_approvals':
      return {
        indexId: 'approvals',
        indexLabel: 'Approvals & Timelines',
        indexNum: '04',
        eyebrow: '',
        heading: '',
        intro: '',
        imageAssetId: null,
        design: {
          title: '',
          checklist: [{ title: '', body: '' }],
          images: [],
        },
        production: {
          title: '',
          intro: '',
          steps: [''],
          warnings: [''],
          images: [],
        },
        installation: {
          title: '',
          paragraphs: [''],
          imageAssetId: null,
        },
        includeInIndex: true,
      };
    case 'process_unique':
      return {
        indexId: 'unique-process',
        indexLabel: 'A Different Kind of Craft',
        indexNum: '05',
        eyebrow: '',
        heading: '',
        paragraphs: [''],
        imageAssetId: null,
        includeInIndex: true,
      };
    case 'trade_intro':
      return { title: '', body: '' };
    case 'trade_card':
      return {
        title: '',
        body: '',
        url: '',
        urlText: '',
        imageAssetId: null,
        imageAlt: '',
        imgAlign: 'left',
      };
    case 'trade_cta':
      return {
        title: '',
        body: '',
        imageAssetId: null,
        imageAlt: '',
        cta1Label: '',
        cta1Url: '',
        cta2Label: '',
        cta2Url: '',
      };
    case 'studio_showrooms':
      return {
        heading: 'Featured Showrooms',
        showrooms: [],
      };
    default:
      return {};
  }
}

export function defaultStudioShowroom() {
  return {
    id: createSectionId('showroom'),
    city: '',
    name: '',
    region: '',
    mapHref: '',
    imageAssetId: null,
    imageAlt: '',
    interiorSlides: [],
  };
}

export function defaultStudioInteriorSlide() {
  return {
    id: createSectionId('slide'),
    imageAssetId: null,
    alt: '',
    durationMs: 6000,
  };
}

function dataToWritePayload(type, data) {
  const next = { ...data };
  if ('imageAssetId' in next || 'image' in next) {
    next.imageAssetId = resolvedImageId(next.imageAssetId ?? next.image);
    delete next.image;
  }
  if ('backgroundImageAssetId' in next) {
    next.backgroundImageAssetId = resolvedImageId(next.backgroundImageAssetId);
  }
  if (Array.isArray(next.images)) {
    next.images = mapImageList(next.images);
  }
  if (Array.isArray(next.logos)) {
    next.logos = mapImageList(next.logos);
  }
  if (Array.isArray(next.bullets)) {
    next.bullets = next.bullets.map(bullet => (typeof bullet === 'string' ? bullet : ''));
  }
  if (Array.isArray(next.paragraphs)) {
    next.paragraphs = next.paragraphs.map(p => (typeof p === 'string' ? p : ''));
  }
  if (Array.isArray(next.items)) {
    next.items = next.items.map(item => ({
      title: item?.title ?? '',
      body: item?.body ?? '',
    }));
  }
  if (Array.isArray(next.stages)) {
    next.stages = next.stages.map(stage => ({
      title: stage?.title ?? '',
      lead: stage?.lead ?? '',
      body: stage?.body ?? '',
    }));
  }
  if (type === 'process_approvals') {
    const design = { ...(next.design || {}) };
    design.checklist = (design.checklist || []).map(item => ({
      title: item?.title ?? '',
      body: item?.body ?? '',
    }));
    design.images = mapImageList(design.images);
    next.design = design;

    const production = { ...(next.production || {}) };
    production.steps = (production.steps || []).map(step => (typeof step === 'string' ? step : ''));
    production.warnings = (production.warnings || []).map(warning =>
      typeof warning === 'string' ? warning : '',
    );
    production.images = mapImageList(production.images);
    next.production = production;

    if (next.finish && typeof next.finish === 'object') {
      const finish = { ...next.finish };
      finish.paragraphs = (finish.paragraphs || []).map(paragraph =>
        typeof paragraph === 'string' ? paragraph : '',
      );
      finish.eyebrow = typeof finish.eyebrow === 'string' ? finish.eyebrow : '';
      finish.imageAssetId = resolvedImageId(finish.imageAssetId ?? finish.image);
      delete finish.image;
      if (finish.imageAssetId == null) delete finish.imageAssetId;
      next.finish = finish;
    }

    const installation = { ...(next.installation || {}) };
    installation.paragraphs = (installation.paragraphs || []).map(p =>
      typeof p === 'string' ? p : '',
    );
    installation.imageAssetId = resolvedImageId(installation.imageAssetId ?? installation.image);
    delete installation.image;
    next.installation = installation;
  }
  if (type === 'hero') {
    if (next.imageAssetId == null) delete next.imageAssetId;
    if (!next.ctaLabel) delete next.ctaLabel;
    if (!next.ctaUrl) delete next.ctaUrl;
    // Always send eyebrow and tagline so CMS edits persist.
    next.eyebrow = typeof next.eyebrow === 'string' ? next.eyebrow : '';
    next.tagline = typeof next.tagline === 'string' ? next.tagline : '';
  }
  if (type === 'trade_card') {
    if (next.imageAssetId == null) delete next.imageAssetId;
    next.imgAlign = next.imgAlign === 'right' ? 'right' : 'left';
    next.urlText = next.urlText ?? '';
    next.url = next.url ?? '';
  }
  if (type === 'trade_cta') {
    if (next.imageAssetId == null) delete next.imageAssetId;
    next.cta1Label = next.cta1Label ?? '';
    next.cta1Url = next.cta1Url ?? '';
    next.cta2Label = next.cta2Label ?? '';
    next.cta2Url = next.cta2Url ?? '';
  }
  if (type === 'studio_showrooms') {
    next.heading = typeof next.heading === 'string' ? next.heading : '';
    next.showrooms = (next.showrooms || []).map(showroom => {
      const entry = {
        id: showroom?.id ?? createSectionId('showroom'),
        city: showroom?.city ?? '',
        name: showroom?.name ?? '',
        region: showroom?.region ?? '',
        mapHref: showroom?.mapHref ?? '',
        imageAlt: showroom?.imageAlt ?? '',
        interiorSlides: (showroom?.interiorSlides || []).map(slide => ({
          id: slide?.id ?? createSectionId('slide'),
          imageAssetId: resolvedImageId(slide?.imageAssetId ?? slide?.image),
          alt: slide?.alt ?? '',
          durationMs:
            typeof slide?.durationMs === 'number' && slide.durationMs >= 1
              ? slide.durationMs
              : 6000,
        })),
      };
      const exteriorId = resolvedImageId(showroom?.imageAssetId ?? showroom?.image);
      if (exteriorId != null) entry.imageAssetId = exteriorId;
      return entry;
    });
  }
  return next;
}

/** Convert read-model sections (resolved images) into write payload (integer ids). */
export function sectionsToWritePayload(sections) {
  return (sections ?? []).map(section => ({
    id: section.id,
    type: section.type,
    data: dataToWritePayload(section.type, section.data || {}),
  }));
}

export function draftSnapshotFromPage(page) {
  return {
    draftSeoTitle: page?.draftSeoTitle ?? '',
    draftSeoDescription: page?.draftSeoDescription ?? '',
    draftOgImageId: resolvedImageId(page?.draftOgImage),
    draftSections: sectionsToWritePayload(page?.draftSections ?? []),
  };
}
